import React, { useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { 
  UserCheck, Calendar,  
  ChevronRight, X, AlertCircle, CheckCircle, Trash2, Filter, Layers
} from 'lucide-react';
import COEHeader from './COEHeader';
import logoutManager from '../logout';
import api from '../api';
import AcademicContextSelector from './AcademicContextSelector';

const AssignEvaluatorToSubject = () => {
  const location = useLocation();
  const [user] = useState(location.state?.user || null);
  const [tenant] = useState(location.state?.tenant || null);
  const [orgInfo] = useState(location.state?.orgInfo || { name: '', logoUrl: '' });
  const [academicContext, setAcademicContext] = useState(null);
  
  const [subjects, setSubjects] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [loadingSubjects, setLoadingSubjects] = useState(false);
  const [statusMessage, setStatusMessage] = useState({ type: '', message: '' });
  
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [availableEvaluators, setAvailableEvaluators] = useState([]);
  const [selectedEvaluator, setSelectedEvaluator] = useState(null);
  const [assigning, setAssigning] = useState(false);
  const [assignedEvaluators, setAssignedEvaluators] = useState([]);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const [evaluatorToRemove, setEvaluatorToRemove] = useState(null);

  const tenantId = localStorage.getItem('tenantId') || tenant?.id || tenant?._id;

  const showStatus = (type, message) => {
    setStatusMessage({ type, message });
    setTimeout(() => setStatusMessage({ type: '', message: '' }), 5000);
  };

  const isContextComplete = useCallback((ctx) => {
    if (!ctx) return false;
    if (!(ctx.programTypeId && ctx.programId && ctx.academicYearId)) return false;
    if (ctx.evaluationMode === 'sem') return !!ctx.semId;
    if (ctx.evaluationMode === 'term') return !!ctx.termId;
    return false;
  }, []);

  const fetchAllSubjectAssignments = useCallback(async (subjectList = []) => {
    if (!tenantId) return;

    try {
      const response = await api.get('/api/coe/evaluators/subject-assignments', {
        params: { tenantId },
        headers: { 'x-tenant-id': tenantId }
      });

      if (response.data.success) {
        const validSubjectIds = new Set((subjectList || []).map(subject => String(subject.subjectId)));
        setAssignedEvaluators((response.data.assignments || []).filter(assignment => validSubjectIds.has(String(assignment.subjectId?._id || assignment.subjectId)) && assignment.status === 'active'));
      }
    } catch (error) {
      console.error('Error fetching subject assignments:', error);
    }
  }, [tenantId]);

  const fetchSubjects = useCallback(async (ctx) => {
    if (!tenantId || !isContextComplete(ctx)) return;
    setLoadingSubjects(true);
    try {
      const params = {
        tenantId,
        programTypeId: ctx.programTypeId,
        programId: ctx.programId,
        academicYearId: ctx.academicYearId,
        evaluationMode: ctx.evaluationMode,
      };
      if (ctx.evaluationMode === 'sem' && ctx.semId) params.semId = ctx.semId;
      else if (ctx.evaluationMode === 'term' && ctx.termId) params.termId = ctx.termId;

      const response = await api.get('/api/coe/end-exam-assignment/subjects', { params, headers: { 'x-tenant-id': tenantId } });
      if (response.data.success) {
        const nextSubjects = (response.data.subjects || []).filter(subject => subject.isLab !== true && subject.subjectType !== 'lab');
        setSubjects(nextSubjects);
        await fetchAllSubjectAssignments(nextSubjects);
      }
    } catch (error) {
      console.error('Error fetching subjects:', error);
      showStatus('error', 'Failed to fetch subjects');
    } finally {
      setLoadingSubjects(false);
    }
  }, [tenantId, isContextComplete, fetchAllSubjectAssignments]);

  const fetchEvaluatorsForSubject = async (subjectId) => {
    try {
      const [evaluatorsRes, assignmentsRes] = await Promise.all([
        api.get('/api/coe/evaluators/list', { params: { tenantId, status: 'active' }, headers: { 'x-tenant-id': tenantId } }),
        api.get('/api/coe/evaluators/subject-assignments/by-subject', { params: { subjectId, tenantId }, headers: { 'x-tenant-id': tenantId } })
      ]);
      if (evaluatorsRes.data.success) setAvailableEvaluators(evaluatorsRes.data.evaluators || []);
      if (assignmentsRes.data.success) setAssignedEvaluators(assignmentsRes.data.assignments || []);
    } catch (error) {
      console.error('Error fetching evaluators:', error);
    }
  };

  const handleContextChange = useCallback((ctx) => {
    setAcademicContext(ctx);
    if (isContextComplete(ctx)) fetchSubjects(ctx);
  }, [fetchSubjects, isContextComplete]);

  const handleAssignEvaluator = async () => {
    if (!selectedSubject || !selectedEvaluator) return;

    if (selectedSubject.hasFinalizedTemplate === false) {
      showStatus('error', 'Please finalize the exam question paper setup first before assigning anyone to this subject.');
      return;
    }

    setAssigning(true);
    try {
      const response = await api.post('/api/coe/evaluators/assign-to-subject', {
        tenantId,
        evaluatorIds: [selectedEvaluator._id],  
        subjectId: selectedSubject.subjectId,
        programTypeId: academicContext.programTypeId,
        programId: academicContext.programId,
        academicYearId: academicContext.academicYearId,
        termId: academicContext.termId || null,
        semId: academicContext.semId || null
      }, { headers: { 'x-tenant-id': tenantId } });
      if (response.data.success) {
        showStatus('success', `${selectedEvaluator.firstName} ${selectedEvaluator.lastName} has been successfully assigned to ${selectedSubject.subjectName}.`);
        setShowAssignModal(false);
        setSelectedEvaluator(null);
        await fetchEvaluatorsForSubject(selectedSubject.subjectId);
      }
    } catch (error) {
      console.error('Error assigning evaluator:', error);
      showStatus('error', error.response?.data?.message || 'Could not assign the evaluator. Please try again.');
    } finally {
      setAssigning(false);
    }
  };

  const handleRemoveEvaluator = async () => {
    if (!evaluatorToRemove) return;
    try {
      const response = await api.delete(`/api/coe/evaluators/subject-assignment/${evaluatorToRemove._id}`, { headers: { 'x-tenant-id': tenantId } });
      if (response.data.success) {
        showStatus('success', `${evaluatorToRemove.evaluatorId?.firstName} ${evaluatorToRemove.evaluatorId?.lastName} was removed from ${selectedSubject?.subjectName}.`);
        setShowRemoveConfirm(false);
        setEvaluatorToRemove(null);
        await fetchEvaluatorsForSubject(selectedSubject.subjectId);
      }
    } catch (error) {
      console.error('Error removing evaluator:', error);
      showStatus('error', 'Could not remove the evaluator. Please try again.');
    }
  };

  const isEvaluatorAssigned = (evaluatorId) => {
    return assignedEvaluators.some(a => a.evaluatorId?._id === evaluatorId && a.status === 'active');
  };

  const getAssignedEvaluatorsForSubject = (subjectId) => {
    return assignedEvaluators.filter(a => String(a.subjectId?._id || a.subjectId) === String(subjectId) && a.status === 'active');
  };

  return (
    <div className="min-h-screen w-full flex flex-col bg-slate-50 text-slate-900 antialiased font-sans">
      <COEHeader user={user} tenant={tenant} orgInfo={orgInfo} onLogout={logoutManager.logout} />

      <main className="flex-1 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        
        {/* Main Title Banner Card */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 md:p-6 transition-all">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-emerald-50 rounded-xl text-emerald-600 shadow-inner">
                <UserCheck className="w-6 h-6 md:w-7 md:h-7" />
              </div>
              <div>
                <h1 className="text-xl md:text-2xl font-bold tracking-tight text-slate-900">Assign Evaluators to Subjects</h1>
                <p className="text-xs md:text-sm text-slate-500 font-medium mt-0.5">Select a course and link teachers or staff members to grade student answer papers.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Academic Context Selector Wrapper */}
        <div className="bg-white rounded-2xl border border-slate-200 p-1 shadow-sm">
          <AcademicContextSelector tenantId={tenantId} onContextChange={handleContextChange} initialContext={academicContext} />
        </div>

        {/* Global Feedback Notifications */}
        {statusMessage.message && (
          <div className={`rounded-xl border p-4 shadow-sm animate-in fade-in slide-in-from-top-4 duration-200 ${
            statusMessage.type === 'error' ? 'bg-red-50 border-red-200 text-red-800' : 
            statusMessage.type === 'success' ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-blue-50 border-blue-200 text-blue-800'
          }`}>
            <div className="flex items-start gap-3">
              {statusMessage.type === 'success' ? <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" /> : <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />}
              <p className="text-sm font-semibold leading-5">{statusMessage.message}</p>
            </div>
          </div>
        )}

        {/* Selected Filters Summary Display */}
        {academicContext && (
          <div className="bg-gradient-to-r from-emerald-50/70 to-teal-50/40 rounded-2xl border border-emerald-100/80 p-5 shadow-sm">
            <div className="flex items-center gap-2 mb-3">
              <Filter className="w-3.5 h-3.5 text-emerald-600" />
              <h3 className="text-xs font-bold uppercase tracking-wider text-emerald-700">Currently Selected Settings</h3>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-y-4 gap-x-6">
              <div className="min-w-0"><p className="text-[10px] uppercase font-bold tracking-wider text-slate-400">Program Type</p><p className="text-xs font-bold text-slate-700 truncate mt-0.5">{academicContext.programTypeName || '—'}</p></div>
              <div className="min-w-0"><p className="text-[10px] uppercase font-bold tracking-wider text-slate-400">Course / Department</p><p className="text-xs font-bold text-slate-700 truncate mt-0.5">{academicContext.programName || '—'}</p></div>
              <div className="min-w-0"><p className="text-[10px] uppercase font-bold tracking-wider text-slate-400">Academic Year</p><p className="text-xs font-bold text-slate-700 truncate mt-0.5">{academicContext.academicYearName || '—'}</p></div>
              <div className="min-w-0"><p className="text-[10px] uppercase font-bold tracking-wider text-slate-400">Term Mode</p><p className="text-xs font-bold text-slate-700 mt-0.5">{academicContext.evaluationMode === 'sem' ? 'Semester System' : 'Term System'}</p></div>
              <div className="min-w-0 col-span-2 sm:col-span-1"><p className="text-[10px] uppercase font-bold tracking-wider text-slate-400">Term Name</p><p className="text-xs font-bold text-emerald-800 mt-0.5 bg-emerald-100/50 px-2 py-0.5 rounded border border-emerald-200/40 inline-block max-w-full truncate">{academicContext.semName || academicContext.termName || '—'}</p></div>
            </div>
          </div>
        )}

        {/* Primary Course Listing Table Panel */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-100 bg-slate-50/70">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-2.5">
                <div className="p-1.5 bg-emerald-100 rounded-lg text-emerald-700 shadow-sm">
                  <Layers className="w-4 h-4" />
                </div>
                <h3 className="text-sm font-bold text-slate-700">List of Subjects</h3>
              </div>
              {loadingSubjects && (
                <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-100 px-3 py-1 rounded-xl animate-pulse">
                  <div className="w-3.5 h-3.5 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin"></div>
                  <span className="text-[11px] font-bold text-emerald-800 uppercase tracking-wider">Loading courses...</span>
                </div>
              )}
            </div>
          </div>

          <div className="divide-y divide-slate-100 max-h-[36rem] overflow-y-auto p-2">
            {!academicContext ? (
              <div className="text-center py-16 text-slate-400">
                <p className="text-sm font-medium">Please pick your academic options above to view the subjects list.</p>
              </div>
            ) : subjects.length === 0 ? (
              <div className="text-center py-16 text-slate-400">
                <p className="text-sm font-medium">No subjects found matching your chosen settings.</p>
              </div>
            ) : (
              <div className="space-y-3 p-2">
                {subjects.map((subject) => {
                  const assignedList = getAssignedEvaluatorsForSubject(subject.subjectId);
                  const hasAssigned = assignedList.length > 0;
                  const isTemplateFinalized = subject.hasFinalizedTemplate === true;
                  return (
                    <div key={subject.subjectId} className="rounded-xl border border-slate-200 bg-white p-4 transition-all hover:shadow-md hover:border-slate-300 flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="min-w-0 flex-1 space-y-2">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h4 className="font-bold text-slate-800 text-sm md:text-base leading-tight truncate">{subject.subjectName}</h4>
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold bg-purple-50 text-purple-700 border border-purple-100">
                            {subject.assessmentType === 'external_only' ? 'External Exam Only' : 'Internal CIE + External ESE'}
                          </span>
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold ${isTemplateFinalized ? 'bg-emerald-50 text-emerald-700 border border-emerald-100' : 'bg-amber-50 text-amber-700 border border-amber-100'}`}>
                            {isTemplateFinalized ? 'Exam Paper Ready' : 'Exam Paper Setup Pending'}
                          </span>
                        </div>
                        
                        <div className="flex items-center gap-3 flex-wrap text-xs font-medium">
                          <span className="inline-flex font-mono font-bold bg-slate-100 text-slate-600 px-2 py-0.5 rounded border">{subject.subjectCode || 'No Code'}</span>
                        </div>

                        {/* Assigned Staff Members Display List */}
                        {hasAssigned && (
                          <div className="bg-slate-50 border border-slate-100 rounded-xl p-2.5 flex flex-wrap items-center gap-2 mt-2">
                            <span className="text-[10px] uppercase font-bold tracking-wider text-slate-400 block mr-1">Assigned Teachers / Staff:</span>
                            <div className="flex flex-wrap gap-1.5">
                              {assignedList.map(assigned => (
                                <span key={assigned._id} className="inline-flex items-center gap-1.5 pl-2.5 pr-1.5 py-1 bg-white border border-slate-200 text-slate-700 rounded-lg text-xs font-semibold shadow-sm group">
                                  {assigned.evaluatorId?.firstName} {assigned.evaluatorId?.lastName}
                                  <button 
                                    onClick={() => { setEvaluatorToRemove(assigned); setShowRemoveConfirm(true); }} 
                                    className="p-0.5 rounded text-slate-400 hover:text-red-600 hover:bg-red-50 transition-all"
                                    title="Remove person from this subject"
                                  >
                                    <X className="w-3 h-3" />
                                  </button>
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {!hasAssigned && isTemplateFinalized && <p className="text-xs font-semibold text-amber-600/90 pl-1">No teachers have been assigned to grade this subject yet.</p>}
                        {!isTemplateFinalized && <p className="text-xs font-semibold text-red-500/90 pl-1">Notice: You must complete and approve the exam question paper setup before you can assign any staff to grade papers.</p>}
                      </div>

                      <div className="border-t md:border-t-0 pt-3 md:pt-0 flex-shrink-0 self-end md:self-center w-full md:w-auto">
                        <button
                          onClick={() => {
                            if (!isTemplateFinalized) {
                              showStatus('error', 'Please complete the question paper setup first before assigning staff.');
                              return;
                            }
                            setSelectedSubject(subject);
                            fetchEvaluatorsForSubject(subject.subjectId);
                            setShowAssignModal(true);
                          }}
                          disabled={!isTemplateFinalized}
                          className="w-full inline-flex items-center justify-center gap-1 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed text-white font-bold rounded-xl text-xs transition-colors shadow-sm"
                        >
                          Add Evaluator <ChevronRight className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </main>

      {/* POP-UP MODAL WINDOW LAYOUTS */}

      {/* Modal 1: Select and Link Staff Member Form */}
      {showAssignModal && selectedSubject && (
        <div className="fixed inset-0 z-[200] bg-slate-900/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4 animate-in fade-in duration-250">
          <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-hidden flex flex-col border border-slate-200 animate-in slide-in-from-bottom-8 duration-200">
            <div className="border-b border-slate-100 px-5 py-4 flex items-center justify-between bg-slate-50/50">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-50 rounded-xl text-emerald-600 shadow-inner">
                  <UserCheck className="w-5 h-5" />
                </div>
                <div>
                  <h2 className="text-base font-bold text-slate-800">Assign Staff Member</h2>
                  <p className="text-xs text-slate-400 font-mono font-bold mt-0.5 truncate max-w-[240px]">{selectedSubject.subjectName} [{selectedSubject.subjectCode || 'No Code'}]</p>
                </div>
              </div>
              <button onClick={() => setShowAssignModal(false)} className="p-1 rounded-full text-slate-400 hover:bg-slate-100 transition-colors"><X className="w-5 h-5" /></button>
            </div>
            
            <div className="p-5 space-y-4 overflow-y-auto flex-1">
              {selectedSubject.hasFinalizedTemplate === false && (
                <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs font-medium text-amber-800 leading-relaxed shadow-sm">
                  System Notice: The exam question paper template needs to be saved and approved before you can connect staff members.
                </div>
              )}
              
              <div className="space-y-1.5">
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-400">Choose a teacher or staff member from the list</label>
                <select 
                  value={selectedEvaluator?._id || ''} 
                  onChange={(e) => { const evaluator = availableEvaluators.find(ev => ev._id === e.target.value); setSelectedEvaluator(evaluator); }} 
                  className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-xs bg-white font-medium text-slate-700 focus:outline-none focus:border-emerald-500 shadow-sm"
                >
                  <option value="">Choose a person...</option>
                  {availableEvaluators.map(evaluator => (
                    <option key={evaluator._id} value={evaluator._id} disabled={isEvaluatorAssigned(evaluator._id)}>
                      {evaluator.firstName} {evaluator.lastName} — ({evaluator.email}) {isEvaluatorAssigned(evaluator._id) ? '[Already Assigned to This Subject]' : ''}
                    </option>
                  ))}
                </select>
              </div>

              {selectedEvaluator && (
                <div className="bg-slate-50 border border-slate-100 rounded-xl p-4 space-y-2 animate-in fade-in zoom-in-95 duration-150">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 border-b pb-1">Selected Person Profile Details</p>
                  <div className="text-xs space-y-1 text-slate-600 font-medium">
                    <p className="font-bold text-slate-800 text-sm">{selectedEvaluator.firstName} {selectedEvaluator.lastName}</p>
                    <p className="font-mono text-slate-400 text-[11px]">{selectedEvaluator.email}</p>
                    <p className="text-slate-400">Phone: {selectedEvaluator.phoneNumber || 'Not provided'}</p>
                    <div className="pt-2 text-[10px] font-semibold text-indigo-600/90 flex items-center gap-1">
                      <Calendar className="w-3 h-3" /> Account Active From: {new Date(selectedEvaluator.temporaryAccess?.timeConstraint?.accessStartDateTime).toLocaleDateString()} to {new Date(selectedEvaluator.temporaryAccess?.timeConstraint?.accessEndDateTime).toLocaleDateString()}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="border-t border-slate-100 px-5 py-3.5 bg-slate-50 flex gap-2 justify-end">
              <button onClick={() => setShowAssignModal(false)} className="px-4 py-2 border border-slate-300 bg-white text-slate-700 font-bold text-xs rounded-xl hover:bg-slate-50 transition-colors">Cancel</button>
              <button 
                onClick={handleAssignEvaluator} 
                disabled={!selectedEvaluator || assigning || isEvaluatorAssigned(selectedEvaluator._id)} 
                className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white font-bold text-xs rounded-xl shadow-sm transition-colors flex items-center justify-center gap-2"
              >
                {assigning && <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>}
                Confirm Assignment
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal 2: Delete / Remove Staff Assignment Confirmation Alert box */}
      {showRemoveConfirm && evaluatorToRemove && (
        <div className="fixed inset-0 z-[200] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-200">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full border border-slate-200 overflow-hidden animate-in zoom-in-95 duration-150">
            <div className="border-b border-slate-100 px-5 py-4 bg-red-50/50 flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-full text-red-600 shadow-sm"><Trash2 className="w-5 h-5" /></div>
              <h2 className="text-sm font-bold text-red-900">Remove Staff Member</h2>
            </div>
            <div className="p-5 space-y-4">
              <p className="text-xs md:text-sm text-slate-600 leading-relaxed font-semibold">
                Are you sure you want to stop <strong className="text-slate-900 font-bold">{evaluatorToRemove.evaluatorId?.firstName} {evaluatorToRemove.evaluatorId?.lastName}</strong> from grading the papers for <strong className="text-slate-900 font-bold">{selectedSubject?.subjectName}</strong>?
              </p>
              <p className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-[11px] font-semibold text-amber-800 leading-normal">
                Important Notice: If you remove this person, any incomplete bundles or boxes of digital answer scripts currently linked to them will automatically become unassigned.
              </p>
              <div className="flex gap-2 justify-end pt-1">
                <button onClick={() => { setShowRemoveConfirm(false); setEvaluatorToRemove(null); }} className="px-4 py-2 border border-slate-300 bg-white text-slate-700 font-bold text-xs rounded-xl hover:bg-slate-50">No, Cancel</button>
                <button onClick={handleRemoveEvaluator} className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-bold text-xs rounded-xl shadow-sm transition-all">Yes, Remove Staff</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AssignEvaluatorToSubject;